import React, { useContext, useState } from 'react';
import { Modal, Button, ListGroup, Form, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { StepContext } from '../../Context/StepContext';
import { FaTrash, FaCartPlus } from 'react-icons/fa';
import CartShowcase from '../CartShowcase'; // Import the updated CartShowcase
import AlertDialog from '../AlertDialog';

const Basket = ({ show, handleClose }) => {
  const { cart, removeItemFromCart, addItemToCart } = useContext(StepContext);
  const navigate = useNavigate();
  const [newItem, setNewItem] = useState({
    bookingDate: '',
    postcode: '',
    motExpiryDate: '',
    firstRegistrationDate: '',
    Model: '',
    fname: '',
    lname: '',
    phone: '',
    carNumber: '',
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Calculate totals for the cart
  const calculateTotals = () => {
    const totalPrice = cart.reduce((sum, item) => {
      return sum + parseInt(item.activePlan?.price?.replace(/,/g, "") || 0);
    }, 0);
    const discount = 50; // Adjust this based on your business logic
    const total = totalPrice - discount;
    return { totalPrice, discount, total };
  };

  const { totalPrice, discount, total } = calculateTotals();

  const handleRemoveItem = async (itemId) => {
    removeItemFromCart(itemId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem({
      ...newItem,
      [name]: value
    });
  };

  const handleAddItem = () => {
    // Basic validation
    if (!newItem.bookingDate || !newItem.carNumber || !newItem.postcode || !newItem.fname) {
      setValidationError('Please fill in all required fields (*)');
      return;
    }
    
    setValidationError('');
    
    // Add a unique ID to the new item
    const itemWithId = {
      ...newItem,
      id: Date.now().toString(), // Simple unique ID generation
      // Add some default fields that CartShowcase expects
      headTitle: `Service for ${newItem.carNumber}`,
      modelName: newItem.Model || 'Unknown Model',
      serviceType: 'Standard Service',
      productDescription: 'Customer added service booking',
    };
    
    // Add item to cart
    addItemToCart(itemWithId);
    
    // Reset form
    setNewItem({
      bookingDate: '',
      postcode: '',
      motExpiryDate: '',
      firstRegistrationDate: '',
      Model: '',
      fname: '',
      lname: '',
      phone: '',
      carNumber: '',
    });
    
    // Hide the add form
    setShowAddForm(false);
    
    // Show success alert
    AlertDialog(
      "Item Added",
      "Service has been added to your cart successfully!",
      "success",
      1500,
      false,
      false,
      "",
      "",
      () => {}, 
      null
    );
  };

  // Handle checkout from cart
  const handleCartCheckout = () => {
    if (cart.length === 0) {
      AlertDialog(
        "Empty Cart",
        "Please add items to your cart before proceeding to checkout.",
        "warning",
        2000,
        true,
        false,
        "OK",
        "",
        () => {}, 
        null
      );
      return;
    }

    // Check if all items have plans selected
    const itemsWithoutPlans = cart.filter(item => !item.activePlan);
    if (itemsWithoutPlans.length > 0) {
      AlertDialog(
        "Plan Selection Required",
        `Please select a plan for all items before proceeding to checkout.
        
        ${itemsWithoutPlans.length} item(s) still need plan selection.`,
        "warning",
        3000,
        true,
        false,
        "OK",
        "",
        () => {}, 
        null
      );
      return;
    }

    // Close modal first
    handleClose();
    
    // Show processing alert
    AlertDialog(
      "Proceeding to Checkout",
      "Taking you to the checkout page...",
      "success",
      800,
      false,
      false,
      "",
      "",
      () => {}, 
      null
    );

    // Navigate to checkout
    setTimeout(() => {
      navigate("/checkout", {
        state: {
          cartItems: cart,
          discount,
          totalPrice,
          total,
        },
      });
    }, 1000);
  };

  return (
    <Modal show={show} onHide={handleClose} size='xl' backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCartPlus className="me-2" />
          Your Cart ({cart.length} items)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          {/* Left side - Add new item form and item management */}
          <div className="col-lg-7">
            {/* Button to toggle add form visibility */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Manage Items</h5>
              <Button 
                variant="primary" 
                onClick={() => setShowAddForm(!showAddForm)}
                className="d-flex align-items-center gap-2"
              >
                <FaCartPlus />
                {showAddForm ? "Cancel" : "Add New Item"}
              </Button>
            </div>
            
            {/* Add to Cart Form */}
            {showAddForm && (
              <div className="mb-4 p-3 border rounded bg-light">
                <h6>Add New Item to Cart</h6>
                {validationError && <Alert variant="danger">{validationError}</Alert>}
                
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Booking Date *</Form.Label>
                      <Form.Control
                        type="date"
                        name="bookingDate"
                        value={newItem.bookingDate}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Postcode *</Form.Label>
                      <Form.Control
                        type="text"
                        name="postcode"
                        value={newItem.postcode}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Last Service Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="motExpiryDate"
                        value={newItem.motExpiryDate}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Build Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="firstRegistrationDate"
                        value={newItem.firstRegistrationDate}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Model</Form.Label>
                      <Form.Control
                        type="text"
                        name="Model"
                        value={newItem.Model}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Registration Number *</Form.Label>
                      <Form.Control
                        type="text"
                        name="carNumber"
                        value={newItem.carNumber}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>First Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="fname"
                        value={newItem.fname}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="lname"
                        value={newItem.lname}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={newItem.phone}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <div className="d-flex justify-content-end mt-3">
                  <Button variant="success" onClick={handleAddItem}>
                    Add to Cart
                  </Button>
                </div>
              </div>
            )}

            {/* Simple list of existing items for quick management */}
            {cart.length > 0 && (
              <div>
                <h6>Current Items ({cart.length})</h6>
                <ListGroup className="mb-3">
                  {cart.map((item, index) => (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{item.headTitle || `Service for ${item.carNumber}`}</strong>
                        <br />
                        <small className="text-muted">
                          {item.carNumber} | {item.bookingDate} | {item.postcode}
                        </small>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveItem(item.id)}
                        title='Remove item from cart'
                      >
                        <FaTrash />
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
          </div>

          {/* Right side - Cart summary using CartShowcase */}
          <div className="col-lg-5">
            <div className="border-start ps-4">
              <h5>Cart Summary</h5>
              {cart.length > 0 ? (
                <CartShowcase 
                  items={cart} 
                  price={[total, totalPrice, discount]}
                  showCheckoutButton={true}
                  onCheckout={handleCartCheckout}
                />
              ) : (
                <div className="text-center py-5">
                  <FaCartPlus size={48} className="text-muted mb-3" />
                  <p className="text-muted">Your cart is empty</p>
                  <p className="small">Add items using the form on the left</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        {cart.length > 0 && (
          <Button variant="primary" onClick={handleCartCheckout}>
            Proceed to Checkout
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default Basket;